import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as Flags from 'country-flag-icons/react/3x2';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface CountryData {
  code: string;
  country: string;
  iso: string; 
  placeholder?: string;
}

function CountryFlag({ iso, title, className }: { iso: string; title?: string; className?: string }) {
  const FlagComponent = (Flags as Record<string, React.ComponentType<{ title?: string; className?: string }>>)[iso];
  if (!FlagComponent) {
    return (
      <span 
        className={cn("text-xs font-medium text-gray-600 min-w-[16px] text-center", className)}
        title={title || `Country: ${iso}`}
      >
        {iso}
      </span>
    );
  }
  return <FlagComponent title={title} className={className} />;
}

// Pinned countries 
const PINNED_COUNTRIES: CountryData[] = [
  { code: '+1', country: 'United States', iso: 'US', placeholder: '201 555 0123' },
  { code: '+61', country: 'Australia', iso: 'AU', placeholder: '412 345 678' },
  { code: '+64', country: 'New Zealand', iso: 'NZ', placeholder: '21 123 4567' },
];

// All other countries (alphabetically sorted)
const OTHER_COUNTRIES: CountryData[] = [
  { code: '+93', country: 'Afghanistan', iso: 'AF' },
  { code: '+355', country: 'Albania', iso: 'AL' },
  { code: '+213', country: 'Algeria', iso: 'DZ' },
  { code: '+376', country: 'Andorra', iso: 'AD' },
  { code: '+244', country: 'Angola', iso: 'AO' },
  { code: '+54', country: 'Argentina', iso: 'AR' },
  { code: '+374', country: 'Armenia', iso: 'AM' },
  { code: '+43', country: 'Austria', iso: 'AT' },
  { code: '+994', country: 'Azerbaijan', iso: 'AZ' },
  { code: '+973', country: 'Bahrain', iso: 'BH' },
  { code: '+880', country: 'Bangladesh', iso: 'BD' },
  { code: '+375', country: 'Belarus', iso: 'BY' },
  { code: '+32', country: 'Belgium', iso: 'BE' },
  { code: '+501', country: 'Belize', iso: 'BZ' },
  { code: '+229', country: 'Benin', iso: 'BJ' },
  { code: '+975', country: 'Bhutan', iso: 'BT' },
  { code: '+591', country: 'Bolivia', iso: 'BO' },
  { code: '+387', country: 'Bosnia and Herzegovina', iso: 'BA' },
  { code: '+267', country: 'Botswana', iso: 'BW' },
  { code: '+55', country: 'Brazil', iso: 'BR' },
  { code: '+673', country: 'Brunei', iso: 'BN' },
  { code: '+359', country: 'Bulgaria', iso: 'BG' },
  { code: '+226', country: 'Burkina Faso', iso: 'BF' },
  { code: '+257', country: 'Burundi', iso: 'BI' },
  { code: '+855', country: 'Cambodia', iso: 'KH' },
  { code: '+237', country: 'Cameroon', iso: 'CM' },
  { code: '+1', country: 'Canada', iso: 'CA', placeholder: '416 555 0123' },
  { code: '+238', country: 'Cape Verde', iso: 'CV' },
  { code: '+236', country: 'Central African Republic', iso: 'CF' },
  { code: '+235', country: 'Chad', iso: 'TD' },
  { code: '+56', country: 'Chile', iso: 'CL' },
  { code: '+86', country: 'China', iso: 'CN' },
  { code: '+57', country: 'Colombia', iso: 'CO' },
  { code: '+269', country: 'Comoros', iso: 'KM' },
  { code: '+242', country: 'Congo', iso: 'CG' },
  { code: '+506', country: 'Costa Rica', iso: 'CR' },
  { code: '+385', country: 'Croatia', iso: 'HR' },
  { code: '+53', country: 'Cuba', iso: 'CU' },
  { code: '+357', country: 'Cyprus', iso: 'CY' },
  { code: '+420', country: 'Czech Republic', iso: 'CZ' },
  { code: '+45', country: 'Denmark', iso: 'DK' },
  { code: '+253', country: 'Djibouti', iso: 'DJ' },
  { code: '+593', country: 'Ecuador', iso: 'EC' },
  { code: '+20', country: 'Egypt', iso: 'EG' },
  { code: '+503', country: 'El Salvador', iso: 'SV' },
  { code: '+240', country: 'Equatorial Guinea', iso: 'GQ' },
  { code: '+291', country: 'Eritrea', iso: 'ER' },
  { code: '+372', country: 'Estonia', iso: 'EE' },
  { code: '+251', country: 'Ethiopia', iso: 'ET' },
  { code: '+679', country: 'Fiji', iso: 'FJ' },
  { code: '+358', country: 'Finland', iso: 'FI' },
  { code: '+33', country: 'France', iso: 'FR' },
  { code: '+241', country: 'Gabon', iso: 'GA' },
  { code: '+220', country: 'Gambia', iso: 'GM' },
  { code: '+995', country: 'Georgia', iso: 'GE' },
  { code: '+49', country: 'Germany', iso: 'DE' },
  { code: '+233', country: 'Ghana', iso: 'GH' },
  { code: '+30', country: 'Greece', iso: 'GR' },
  { code: '+502', country: 'Guatemala', iso: 'GT' },
  { code: '+224', country: 'Guinea', iso: 'GN' },
  { code: '+592', country: 'Guyana', iso: 'GY' },
  { code: '+509', country: 'Haiti', iso: 'HT' },
  { code: '+504', country: 'Honduras', iso: 'HN' },
  { code: '+852', country: 'Hong Kong', iso: 'HK' },
  { code: '+36', country: 'Hungary', iso: 'HU' },
  { code: '+354', country: 'Iceland', iso: 'IS' },
  { code: '+91', country: 'India', iso: 'IN' },
  { code: '+62', country: 'Indonesia', iso: 'ID' },
  { code: '+98', country: 'Iran', iso: 'IR' },
  { code: '+964', country: 'Iraq', iso: 'IQ' },
  { code: '+353', country: 'Ireland', iso: 'IE' },
  { code: '+972', country: 'Israel', iso: 'IL' },
  { code: '+39', country: 'Italy', iso: 'IT' },
  { code: '+225', country: 'Ivory Coast', iso: 'CI' },
  { code: '+81', country: 'Japan', iso: 'JP' },
  { code: '+962', country: 'Jordan', iso: 'JO' },
  { code: '+7', country: 'Kazakhstan', iso: 'KZ' },
  { code: '+254', country: 'Kenya', iso: 'KE' },
  { code: '+965', country: 'Kuwait', iso: 'KW' },
  { code: '+996', country: 'Kyrgyzstan', iso: 'KG' },
  { code: '+856', country: 'Laos', iso: 'LA' },
  { code: '+371', country: 'Latvia', iso: 'LV' },
  { code: '+961', country: 'Lebanon', iso: 'LB' },
  { code: '+266', country: 'Lesotho', iso: 'LS' },
  { code: '+231', country: 'Liberia', iso: 'LR' },
  { code: '+218', country: 'Libya', iso: 'LY' },
  { code: '+423', country: 'Liechtenstein', iso: 'LI' },
  { code: '+370', country: 'Lithuania', iso: 'LT' },
  { code: '+352', country: 'Luxembourg', iso: 'LU' },
  { code: '+853', country: 'Macau', iso: 'MO' },
  { code: '+389', country: 'North Macedonia', iso: 'MK' },
  { code: '+261', country: 'Madagascar', iso: 'MG' },
  { code: '+265', country: 'Malawi', iso: 'MW' },
  { code: '+60', country: 'Malaysia', iso: 'MY' },
  { code: '+960', country: 'Maldives', iso: 'MV' },
  { code: '+223', country: 'Mali', iso: 'ML' },
  { code: '+356', country: 'Malta', iso: 'MT' },
  { code: '+222', country: 'Mauritania', iso: 'MR' },
  { code: '+230', country: 'Mauritius', iso: 'MU' },
  { code: '+52', country: 'Mexico', iso: 'MX' },
  { code: '+373', country: 'Moldova', iso: 'MD' },
  { code: '+377', country: 'Monaco', iso: 'MC' },
  { code: '+976', country: 'Mongolia', iso: 'MN' },
  { code: '+382', country: 'Montenegro', iso: 'ME' },
  { code: '+212', country: 'Morocco', iso: 'MA' },
  { code: '+258', country: 'Mozambique', iso: 'MZ' },
  { code: '+95', country: 'Myanmar', iso: 'MM' },
  { code: '+264', country: 'Namibia', iso: 'NA' },
  { code: '+977', country: 'Nepal', iso: 'NP' },
  { code: '+31', country: 'Netherlands', iso: 'NL' },
  { code: '+505', country: 'Nicaragua', iso: 'NI' },
  { code: '+227', country: 'Niger', iso: 'NE' },
  { code: '+234', country: 'Nigeria', iso: 'NG' },
  { code: '+850', country: 'North Korea', iso: 'KP' },
  { code: '+47', country: 'Norway', iso: 'NO' },
  { code: '+968', country: 'Oman', iso: 'OM' },
  { code: '+92', country: 'Pakistan', iso: 'PK' },
  { code: '+970', country: 'Palestine', iso: 'PS' },
  { code: '+507', country: 'Panama', iso: 'PA' },
  { code: '+675', country: 'Papua New Guinea', iso: 'PG' },
  { code: '+595', country: 'Paraguay', iso: 'PY' },
  { code: '+51', country: 'Peru', iso: 'PE' },
  { code: '+63', country: 'Philippines', iso: 'PH' },
  { code: '+48', country: 'Poland', iso: 'PL' },
  { code: '+351', country: 'Portugal', iso: 'PT' },
  { code: '+974', country: 'Qatar', iso: 'QA' },
  { code: '+40', country: 'Romania', iso: 'RO' },
  { code: '+7', country: 'Russia', iso: 'RU' },
  { code: '+250', country: 'Rwanda', iso: 'RW' },
  { code: '+966', country: 'Saudi Arabia', iso: 'SA' },
  { code: '+221', country: 'Senegal', iso: 'SN' },
  { code: '+381', country: 'Serbia', iso: 'RS' },
  { code: '+65', country: 'Singapore', iso: 'SG' },
  { code: '+421', country: 'Slovakia', iso: 'SK' },
  { code: '+386', country: 'Slovenia', iso: 'SI' },
  { code: '+252', country: 'Somalia', iso: 'SO' },
  { code: '+27', country: 'South Africa', iso: 'ZA' },
  { code: '+82', country: 'South Korea', iso: 'KR' },
  { code: '+211', country: 'South Sudan', iso: 'SS' },
  { code: '+34', country: 'Spain', iso: 'ES' },
  { code: '+94', country: 'Sri Lanka', iso: 'LK' },
  { code: '+249', country: 'Sudan', iso: 'SD' },
  { code: '+597', country: 'Suriname', iso: 'SR' },
  { code: '+46', country: 'Sweden', iso: 'SE' },
  { code: '+41', country: 'Switzerland', iso: 'CH' },
  { code: '+963', country: 'Syria', iso: 'SY' },
  { code: '+886', country: 'Taiwan', iso: 'TW' },
  { code: '+992', country: 'Tajikistan', iso: 'TJ' },
  { code: '+255', country: 'Tanzania', iso: 'TZ' },
  { code: '+66', country: 'Thailand', iso: 'TH' },
  { code: '+228', country: 'Togo', iso: 'TG' },
  { code: '+676', country: 'Tonga', iso: 'TO' },
  { code: '+216', country: 'Tunisia', iso: 'TN' },
  { code: '+90', country: 'Turkey', iso: 'TR' },
  { code: '+993', country: 'Turkmenistan', iso: 'TM' },
  { code: '+256', country: 'Uganda', iso: 'UG' },
  { code: '+380', country: 'Ukraine', iso: 'UA' },
  { code: '+971', country: 'United Arab Emirates', iso: 'AE' },
  { code: '+44', country: 'United Kingdom', iso: 'GB' },
  { code: '+598', country: 'Uruguay', iso: 'UY' },
  { code: '+998', country: 'Uzbekistan', iso: 'UZ' },
  { code: '+678', country: 'Vanuatu', iso: 'VU' },
  { code: '+58', country: 'Venezuela', iso: 'VE' },
  { code: '+84', country: 'Vietnam', iso: 'VN' },
  { code: '+967', country: 'Yemen', iso: 'YE' },
  { code: '+260', country: 'Zambia', iso: 'ZM' },
  { code: '+263', country: 'Zimbabwe', iso: 'ZW' },
];

// Combined list for parsing (pinned first, then others)
const ALL_COUNTRIES: CountryData[] = [...PINNED_COUNTRIES, ...OTHER_COUNTRIES];

// For backwards compatibility
const COUNTRY_CODES = ALL_COUNTRIES;

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  defaultCountryCode?: string;
}

function getCountryCodeByName(countryName?: string | null): string | undefined {
  if (!countryName) return undefined;
  const country = ALL_COUNTRIES.find(
    (c) => c.country.toLowerCase() === countryName.toLowerCase()
  );
  return country?.code;
}

function parsePhoneNumber(fullNumber: string, defaultCode?: string): { countryCode: string; localNumber: string } {
  if (!fullNumber) {
    return { countryCode: defaultCode || '', localNumber: '' };
  }

  // Only parse numbers that start with + (proper international format)
  if (fullNumber.startsWith('+')) {
    const sortedCountries = [...ALL_COUNTRIES].sort((a, b) => b.code.length - a.code.length);

    for (const { code } of sortedCountries) {
      if (fullNumber.startsWith(code)) {
        return {
          countryCode: code,
          localNumber: fullNumber.slice(code.length),
        };
      }
    }
  }

  // For legacy numbers without +, keep entire number as local
  return { countryCode: '', localNumber: fullNumber };
}

function formatLocalNumber(input: string): string {
  let cleaned = input.replace(/[^\d]/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.slice(1);
  }
  return cleaned;
}

function combinePhoneNumber(countryCode: string, localNumber: string): string {
  const formatted = formatLocalNumber(localNumber);
  if (!formatted) {
    return '';
  }
  return `${countryCode}${formatted}`;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value = '', onChange, placeholder, className, disabled, defaultCountryCode }, ref) => {
    const { countryCode: initialCountryCode, localNumber: initialLocalNumber } =
      parsePhoneNumber(value, defaultCountryCode);

    const [countryCode, setCountryCode] = React.useState(initialCountryCode || '');
    const [localNumber, setLocalNumber] = React.useState(initialLocalNumber);
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');

    React.useEffect(() => {
      const { countryCode: newCountryCode, localNumber: newLocalNumber } = parsePhoneNumber(value, defaultCountryCode);
      setCountryCode(newCountryCode);
      setLocalNumber(newLocalNumber);
    }, [value, defaultCountryCode]);

    const handleCountryCodeChange = (newCode: string) => {
      setCountryCode(newCode);
      setOpen(false);
      setSearchQuery('');
      const combined = combinePhoneNumber(newCode, localNumber);
      onChange?.(combined);
    };

    const handleLocalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawInput = e.target.value;
      setLocalNumber(rawInput);
      
      // Only combine if country code is selected
      if (countryCode) {
        const combined = combinePhoneNumber(countryCode, rawInput);
        onChange?.(combined);
      } else {
        // Keep as-is for legacy numbers without country code
        onChange?.(rawInput);
      }
    };

    const selectedCountry = ALL_COUNTRIES.find((c) => c.code === countryCode);
    const currentPlaceholder =
      placeholder || selectedCountry?.placeholder || 'Phone number';

    return (
      <div className={cn('flex gap-2', className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[120px] shrink-0 justify-between px-2"
              disabled={disabled}
            >
              <span className="flex items-center gap-1.5 truncate">
                {countryCode && selectedCountry ? (
                  <>
                    <CountryFlag iso={selectedCountry.iso} title={selectedCountry.country} className="h-3.5 w-5 shrink-0" />
                    <span>{countryCode}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </span>
              <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput
                placeholder="Search country/region..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No country/region found.</CommandEmpty>
                <CommandGroup heading="Frequently Used">
                  {PINNED_COUNTRIES.map((country) => (
                    <CommandItem
                      key={`pinned-${country.code}-${country.country}`}
                      value={`${country.country} ${country.code}`}
                      onSelect={() => handleCountryCodeChange(country.code)}
                    >
                      <CountryFlag iso={country.iso} title={country.country} className="mr-2 h-3.5 w-5 shrink-0" />
                      <span className="flex-1">{country.country}</span>
                      <span className="text-muted-foreground">{country.code}</span>
                      <Check
                        className={cn(
                          'ml-2 h-4 w-4',
                          countryCode === country.code ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup heading="All Countries & Regions">
                  {OTHER_COUNTRIES.map((country) => (
                    <CommandItem
                      key={`other-${country.code}-${country.country}`}
                      value={`${country.country} ${country.code}`}
                      onSelect={() => handleCountryCodeChange(country.code)}
                    >
                      <CountryFlag iso={country.iso} title={country.country} className="mr-2 h-3.5 w-5 shrink-0" />
                      <span className="flex-1">{country.country}</span>
                      <span className="text-muted-foreground">{country.code}</span>
                      <Check
                        className={cn(
                          'ml-2 h-4 w-4',
                          countryCode === country.code ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          ref={ref}
          type="tel"
          value={localNumber}
          onChange={handleLocalNumberChange}
          placeholder={currentPlaceholder}
          disabled={disabled}
          className="flex-1"
        />
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput, parsePhoneNumber, formatLocalNumber, combinePhoneNumber, getCountryCodeByName, COUNTRY_CODES, ALL_COUNTRIES, PINNED_COUNTRIES };
export type { PhoneInputProps, CountryData };
